import { WebpageTreeNode, WebpageWithRisk } from './types';

interface Accum {
  name: string;
  path: string; // folders: cumulative pathname; leaves: the webpage key (unique)
  folders: Map<string, Accum>;
  leaves: Accum[];
  webpage?: WebpageWithRisk;
}

function makeAccum(name: string, path: string): Accum {
  return { name, path, folders: new Map(), leaves: [] };
}

/**
 * Splits a URL into its path segments and a leaf display name. The leaf name is
 * the last path segment plus the query string and fragment, so pages that share
 * a pathname but differ by query (e.g. `/Account/Login?ReturnUrl=%2F`) get
 * distinct labels. Malformed URLs fall back to the raw value as a root leaf.
 */
function parseUrl(url: string): { segments: string[]; leafName: string } {
  try {
    const parsed = new URL(url);
    const segments = parsed.pathname.split('/').filter(Boolean);
    const last = segments.length > 0 ? segments[segments.length - 1] : '/';
    return { segments, leafName: `${last}${parsed.search}${parsed.hash}` };
  } catch {
    return { segments: [], leafName: url };
  }
}

/**
 * Builds a collapsible tree from a flat list of webpages.
 *
 * - Folders come from the URL pathname DIRECTORIES (all segments but the last).
 * - Each distinct webpage is its own leaf, labelled with its query string, so
 *   query-variants of the same path (e.g. `/Account/Login` and
 *   `/Account/Login?ReturnUrl=%2F`) are separate rows — not merged.
 * - A page whose full pathname is ALSO a directory of other pages (e.g.
 *   `/lib/js/dropdown` when `/lib/js/dropdown/attachBody` exists) is attached to
 *   that folder node, so it renders as one expandable + clickable row.
 *
 * Filtering (by active signals) is applied to the `webpages` list BEFORE this
 * runs, so empty folders are never created.
 */
export function buildWebpageTree(
  webpages: WebpageWithRisk[]
): WebpageTreeNode[] {
  const parsed = webpages.map(webpage => ({
    webpage,
    ...parseUrl(webpage.url),
  }));

  // A cumulative path is a directory when it is a strict prefix of some page.
  const directoryPaths = new Set<string>();
  for (const { segments } of parsed) {
    let cumulative = '';
    for (let i = 0; i < segments.length - 1; i++) {
      cumulative = cumulative ? `${cumulative}/${segments[i]}` : segments[i];
      directoryPaths.add(cumulative);
    }
  }

  const root = makeAccum('', '');
  const getFolder = (segments: string[]): Accum => {
    let node = root;
    let cumulative = '';
    for (const segment of segments) {
      cumulative = cumulative ? `${cumulative}/${segment}` : segment;
      let next = node.folders.get(segment);
      if (!next) {
        next = makeAccum(segment, cumulative);
        node.folders.set(segment, next);
      }
      node = next;
    }
    return node;
  };

  for (const { webpage, segments, leafName } of parsed) {
    const fullPath = segments.join('/');
    // Only the BARE directory path (no query/hash) collapses onto its folder
    // node — its leaf name equals the last segment. Query-string variants of a
    // directory path (e.g. `/api?x=1` when `/api` is also a folder) keep their
    // query in `leafName`, so they fall through to the leaf branch and render
    // as distinct rows instead of overwriting the folder's single webpage.
    const isBareDirectoryPath =
      segments.length > 0 && leafName === segments[segments.length - 1];
    if (isBareDirectoryPath && directoryPaths.has(fullPath)) {
      // The page is also a directory of other pages — one expandable row.
      getFolder(segments).webpage = webpage;
    } else {
      // A distinct leaf under its parent directory.
      const parent = getFolder(segments.slice(0, -1));
      const leaf = makeAccum(leafName, webpage.key);
      leaf.webpage = webpage;
      parent.leaves.push(leaf);
    }
  }

  return toNodes(root);
}

function toNodes(accum: Accum): WebpageTreeNode[] {
  const folders = [...accum.folders.values()]
    .sort((a, b) => a.name.localeCompare(b.name))
    .map<WebpageTreeNode>(folder => {
      const children = toNodes(folder);
      return {
        name: folder.name,
        path: folder.path,
        webpage: folder.webpage,
        ...(children.length > 0 ? { children } : {}),
      };
    });

  const leaves = [...accum.leaves]
    .sort((a, b) => a.name.localeCompare(b.name))
    .map<WebpageTreeNode>(leaf => ({
      name: leaf.name,
      path: leaf.path,
      webpage: leaf.webpage,
    }));

  // Folders (expandable) first, then leaf pages — tree-command order.
  return [...folders, ...leaves];
}

/** Every expandable node's path (used for Show all + filter auto-expand). */
export function collectFolderPaths(nodes: WebpageTreeNode[]): string[] {
  const paths: string[] = [];
  for (const node of nodes) {
    if (node.children) {
      paths.push(node.path);
      paths.push(...collectFolderPaths(node.children));
    }
  }
  return paths;
}
